// A practice of one store (DVN's answers of 28 Oct 2026, change 13; BR-TENANCY-003; the Sprint 4 board's question 66): an
// organisation with exactly one Active branch, as most practices that buy Vision X are. Sprint 4's screens offer no choice of
// branch there and nothing that would repeat the practice's own at its one branch, and a second Active branch brings them back
// ("a practice of one store" in docs/use-cases/pricing.md, imports.md, inventory.md and catalogue.md). The data and the routes are
// the same; only the screens differ.
//
// Who knows it: an Owner's or Admin's session lists every Active branch (TEN-26), so it says so itself; anyone else's lists only
// the branches they are assigned to, so two or more say the organisation has several, and one is read against the organisation's
// branch list, which every role reads (`GET /branches`, `tenancy.branch.read`) — the list Patients reads for branch names, under
// the same query key. That list, once read, decides over the session: a branch added in Settings › Branches, which reads the list
// again, brings every choice back though the session still lists one (UC-INVENTORY-001 A8 step 3). Until the list answers — or
// should it fail — anyone but an Owner or Admin is shown what a practice of several branches sees: nothing is hidden on a guess.
import { useQuery } from "@tanstack/react-query";
import { branches } from "../api/endpoints";
import { can, useSessionView } from "./store";

/** The organisation's branch list, as every role may read it; cached as Patients' branch names are. */
export const BRANCH_LIST_KEY = ["branches"] as const;

/** True only when the organisation is known to have exactly one Active branch. */
export function useOneStore(): boolean {
  const s = useSessionView();
  const active = (s?.switchableBranches ?? []).filter((b) => b.status === "Active").length;
  const list = useQuery({
    queryKey: BRANCH_LIST_KEY, queryFn: () => branches.list(), staleTime: 5 * 60_000,
    // Two branches in the session are two in the organisation: no read is needed for them.
    enabled: s !== null && active <= 1 && can(s, "tenancy.branch.read"),
  });
  if (!s) return false;
  const page = list.data?.data;
  if (page) return !page.nextCursor && page.items.filter((b) => b.status === "Active").length === 1;
  return (s.user.role === "Owner" || s.user.role === "Admin") && active === 1;
}
